import { Button, Checkbox, Input, Modal, Radio, Tooltip } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import { Translate } from '@above-the-treeline/localization';
import api from '../../Api';
import { connectToDownloads } from '../Downloads/containers/DownloadsDataStore';
import LocalizedDate from '../Localization/LocalizedDate';
import { connectToUserPreferences } from '../UserPreferences';
import combineConnectors from '../util/combineConnectors';
import { drmMethods } from './DrmMethods';
import ReviewCopyActionTooltip from './ReviewCopyActionTooltip';
import EdelweissReaderLink from './EdelweissReaderLink';
import { connectToAccountOrMembershipRequiredModalContext } from '../Anonymous/AccountOrMembershipRequiredModal/contexts/AccountOrMembershipRequiredModalContext';
import { kindleDownloadMethods } from './KindleDownloadMethods';
import { requestFormats } from './RequestFormats';
import {
	doOpenReviewCopyAction,
	deactivateAutoOpenReviewCopyAction,
} from './routines';
import './reviewCopy.less';
import { connectToUser } from '../User/containers/UserDataStore';
import { withIsMobile } from 'treeline-react-core-components/WindowSizeProvider';
import ApprovedIcon from '../UI/ApprovedIcon';

export class ReviewCopyDownloadModalAnchor extends React.Component {
	static propTypes = {
		title: PropTypes.object,
		tooltipPlacement: PropTypes.string,
		downloadDate: PropTypes.string,
		digitalCreatedDate: PropTypes.string,
		digitalUpdatedDate: PropTypes.string,
		doAllowOpenOnMount: PropTypes.bool,
		isMobile: PropTypes.func,
	};

	static defaultProps = {
		doAllowOpenOnMount: false,
	};

	steps = {
		unknown: -1,
		selectFormat: 1,
		kindleInfo: 2,
		kindleOverview: 3,
		standardOverview: 4,
		audioOverview: 5,
		audioDownload: 6,
		externalUrlOverview: 7,
	};

	state = {
		isModalOpen: false,
		changeKindleEmailModalOpen: false,
		currentStep: this.steps.unknown,
		selectedFormat: requestFormats.unknown,
		selectedKindleDownloadMethod: kindleDownloadMethods.sendToEmail,
		kindleEmail: '',
		kindleEmailLocked: false,
		hasInitialKindleEmail: false,
		doSaveAsDefaultFormat: false,
		sending: false,
		success: false,
		failure: false,
	};

	componentDidMount() {
		if (this.props.doAllowOpenOnMount && doOpenReviewCopyAction()) {
			this.openModal();
			deactivateAutoOpenReviewCopyAction();
		}
	}

	render = () => {
		const { downloadDate, tooltipPlacement } = this.props;

		const renderButton = () => {
			return (
				<div className="column-narrow">
					<Button shape="circle" onClick={this.handleClick}>
						<div className="relative">
							<div
								className={`icon-cloud-download ${
									downloadDate
										? 'cloud-downloaded'
										: 'cloud-download'
								}`}
							/>
							{downloadDate && (
								<ApprovedIcon style={{ bottom: 8, right: 4 }} />
							)}
						</div>
					</Button>
				</div>
			);
		};

		const renderButtonWithTooltip = () => {
			return (
				<Tooltip
					title={this.getTooltip(downloadDate)}
					placement={tooltipPlacement || 'topRight'}>
					{renderButton()}
					<div className="clear" />
				</Tooltip>
			);
		};

		return (
			<div>
				{this.props.isMobile()
					? renderButton()
					: renderButtonWithTooltip()}
				<Modal
					width={700}
					className="download-and-read-modal"
					visible={this.state.isModalOpen}
					title={this.getModalTitle()}
					bodyStyle={this.getModalBodyStyle()}
					onCancel={() => this.setState({ isModalOpen: false })}
					footer={this.getModalButtons()}>
					{this.renderModalContents()}
				</Modal>
				<Modal
					visible={this.infoModalVisible()}
					closable={false}
					footer={[
						<Button
							type="primary"
							disabled={this.state.sending}
							onClick={() => {
								this.setState({
									sending: false,
									success: false,
									failure: false,
									message: '',
								});
							}}>
							<Translate id="ok" />
						</Button>,
					]}>
					{this.renderInfoModalContents()}
				</Modal>
				<Modal
					visible={this.state.changeKindleEmailModalOpen}
					closable={false}
					title={<Translate id="change_email_address" />}
					onCancel={() =>
						this.setState({ changeKindleEmailModalOpen: false })
					}
					onOk={() =>
						this.setState({
							changeKindleEmailModalOpen: false,
							kindleEmailLocked: false,
						})
					}>
					<Translate id="kindle_email_disclaimer" />
				</Modal>
			</div>
		);
	};

	handleClick = () => {
		const {
			userDataStore: { isAnonymous },
			accountOrMembershipRequiredModalContext: { setIsVisible },
		} = this.props;
		return isAnonymous() ? setIsVisible(true) : this.openModal();
	};

	openModal = () => {
		const {
			kindleDownloadMethod,
			kindleEmail,
			preferredFormat,
		} = this.props.userPreferences.getReviewCopyDownloadPreferences();

		const hasInitialKindleEmail = kindleEmail && kindleEmail.length > 0;
		const availableFormats = this.getAvailableFormats();
		let selectedFormat = requestFormats.unknown;
		let currentStep = this.steps.selectFormat;

		if (availableFormats) {
			if (availableFormats.length === 1) {
				selectedFormat = availableFormats[0].format;
			} else if (
				preferredFormat &&
				availableFormats.some(f => f.format === preferredFormat)
			) {
				selectedFormat = preferredFormat;
			} else if (this.hasPdfFormat()) {
				selectedFormat = requestFormats.pdf;
			} else {
				selectedFormat = availableFormats[0].format;
			}
		}

		switch (selectedFormat) {
			case requestFormats.unknown:
				currentStep = this.steps.selectFormat;
				break;
			case requestFormats.kindle:
				currentStep = this.steps.kindleOverview;
				break;
			case requestFormats.externalUrl:
				currentStep = this.steps.externalUrlOverview;
				break;
			default:
				currentStep = this.steps.standardOverview;
		}

		this.setState({
			isModalOpen: true,
			currentStep,
			selectedFormat,
			kindleEmail,
			selectedKindleDownloadMethod: kindleDownloadMethod,
			hasInitialKindleEmail,
			kindleEmailLocked: hasInitialKindleEmail,
		});
	};

	getFormatName = format => {
		switch (format) {
			case requestFormats.kindle:
				return 'Kindle';
			case requestFormats.audioZip:
			case requestFormats.audioChapters:
			case requestFormats.mp3:
				return 'Audio';
			default:
				return 'Standard';
		}
	};

	getAudioZipFormatInfo = () =>
		this.getAvailableFormats().filter(
			f => f.format === requestFormats.audioZip,
		);

	getAudioChaptersFormatInfo = () =>
		this.getAvailableFormats().filter(
			f => f.format === requestFormats.audioChapters,
		);

	getFormatInfo = format =>
		this.getAvailableFormats().find(f => f.format === format);

	getFormatResourceId = format => this.getFormatInfo(format).resourceId;

	getFormatDrmMethod = format => this.getFormatInfo(format).drmMethod;

	isOpenDrmMethod = format =>
		this.getFormatDrmMethod(format) === drmMethods.open;

	getTitle = () => this.props.title;

	getSku = () => this.getTitle().sku;

	getTitleName = () => this.getTitle().name;

	getTitleUri = () => this.getTitleUri().uri;

	getAvailableFormats = () => this.getTitle().availableReviewCopyFormats;

	hasMultipleFormatsAvailable = () =>
		this.getAvailableFormats() && this.getAvailableFormats().length > 1;

	hasFormat = format =>
		this.getAvailableFormats() &&
		this.getAvailableFormats().some(f => f.format === format);

	hasEPubFormat = () => this.hasFormat(requestFormats.ePub);

	hasKindleFormat = () => this.hasFormat(requestFormats.kindle);

	hasPdfFormat = () => this.hasFormat(requestFormats.pdf);

	hasMobiFormat = () => this.hasFormat(requestFormats.mobi);

	hasExternalUrlFormat = () => this.hasFormat(requestFormats.externalUrl);

	hasAudioZipFormat = () => this.hasFormat(requestFormats.audioZip);

	hasAudioChaptersFormat = () => this.hasFormat(requestFormats.audioChapters);

	hasMp3Format = () => this.hasFormat(requestFormats.mp3);

	hadDigitalFormat = () =>
		this.hasEPubFormat() ||
		this.hasKindleFormat() ||
		this.hasPdfFormat() ||
		this.hasMobiFormat() ||
		this.hasExternalUrlFormat();

	hasAudioFormat = () =>
		this.hasAudioChaptersFormat() ||
		this.hasAudioZipFormat() ||
		this.hasMp3Format();

	getFormatDescription = format => {
		switch (format) {
			case requestFormats.pdf:
			case requestFormats.ePub:
				return <Translate id="drc_standard_description" />;
			case requestFormats.kindle:
				return <Translate id="drc_kindle_description" />;
			case requestFormats.audioChapters:
			case requestFormats.audioZip:
			case requestFormats.mp3:
				return <Translate id="drc_audio_description" />;
		}
	};

	getTooltip(downloadDate) {
		const { digitalCreatedDate, digitalUpdatedDate } = this.props;
		if (downloadDate) {
			return (
				<React.Fragment>
					<Translate id="drc_downloaded" />
					&nbsp;
					<LocalizedDate dateString={downloadDate} />
				</React.Fragment>
			);
		} else {
			return (
				<ReviewCopyActionTooltip
					headerText={'Download DRC'}
					createdDate={digitalCreatedDate}
					updatedDate={digitalUpdatedDate}
				/>
			);
		}
	}

	infoModalVisible = () =>
		this.state.sending || this.state.success || this.state.failure;

	renderInfoSendingModalContents = () => {
		const { message } = this.state;

		return (
			<div>
				<b>
					<Translate id="sending" />
					:&nbsp;
				</b>
				<span>{message}</span>
			</div>
		);
	};

	renderInfoSuccessModalContents = () => {
		const title = this.getTitle();

		return (
			<div>
				<b className="success">Success:&nbsp;</b>
				<span>
					Request sent for <b>{title.name}</b>.
				</span>
			</div>
		);
	};

	renderInfoFailureModalContents = () => {
		const { message } = this.state;

		return (
			<div>
				<b className="error">
					<Translate id="error" />
					:&nbsp;
				</b>
				<span>{message}</span>
			</div>
		);
	};

	renderInfoModalContents = () => {
		const { sending, success, failure } = this.state;

		if (sending) {
			return this.renderInfoSendingModalContents();
		}

		if (success) {
			return this.renderInfoSuccessModalContents();
		}

		if (failure) {
			return this.renderInfoFailureModalContents();
		}

		return null;
	};

	getBackButton = step => (
		<Button key="back" onClick={() => this.handleBackButton(step)}>
			<Translate id="back" />
		</Button>
	);

	getContinueButton = () => (
		<Button
			key="submit"
			type="primary"
			onClick={() => this.handleSubmitButton()}>
			<Translate id="continue" />
		</Button>
	);

	getSubmitButton = () => (
		<Button
			key="submit"
			type="primary"
			onClick={() => this.handleSubmitButton()}>
			<Translate id="download" />
		</Button>
	);

	getEdelweissReaderLink = () => (
		<EdelweissReaderLink title={this.getTitle()} buttonType="ghost" />
	);

	getModalButtons = () => {
		const { currentStep } = this.state;
		const buttons = [];

		switch (currentStep) {
			case this.steps.kindleInfo:
				buttons.push(this.getBackButton(this.steps.selectFormat));
				buttons.push(this.getContinueButton());
				break;
			case this.steps.audioOverview:
			case this.steps.selectFormat:
			case this.steps.externalUrlOverview:
				buttons.push(this.getContinueButton());
				break;
			default:
				buttons.push(this.getEdelweissReaderLink());
				buttons.push(this.getSubmitButton());
		}

		return buttons;
	};

	handleBackButton = step => this.goToStep(step);

	handleSubmitButton = async () => {
		const { currentStep } = this.state;

		switch (currentStep) {
			case this.steps.selectFormat:
				return this.submitSelectFormat();
			case this.steps.kindleInfo:
				return this.submitKindleInfo();
			case this.steps.kindleOverview:
				return await this.submitKindleDownload();
			case this.steps.standardOverview:
				return this.submitStandardDownload();
			case this.steps.audioOverview:
				return this.goToAudioDownloadStep();
			case this.steps.externalUrlOverview:
				return this.openExternalDrcUrl();
			case this.steps.audioDownload:
			default:
		}
	};

	openExternalDrcUrl = () => {
		const { selectedFormat } = this.state;
		const format = this.getFormatInfo(selectedFormat);

		if (format && format.resourceUri) {
			window.open(format.resourceUri);
		}
	};

	submitStandardDownload = () => {
		const { selectedFormat } = this.state;
		const resourceId = this.getFormatResourceId(selectedFormat);

		window.open(
			`${process.env.REACT_APP_API_URL}/api/v1/me/galleys/${resourceId}/download`,
		);

		this.props.downloadsDataStore.refresh();
	};

	submitKindleDownload = async () => {
		const { selectedFormat } = this.state;
		const resourceId = this.getFormatResourceId(selectedFormat);

		this.setState({
			sending: true,
			success: false,
			failure: false,
			message: 'Kindle download request',
		});

		if (!resourceId) {
			return;
		}

		try {
			await api.get(`/api/v1/me/galleys/${resourceId}/download`);

			this.setState({
				sending: false,
				success: true,
				failure: false,
				message: '',
			});
		} catch (err) {
			console.error(err);

			this.setState({
				sending: false,
				success: false,
				failure: true,
				message:
					'An error occurred while attempting to send this title to your Kindle.  Please contact Edelweiss customer support for assistance.',
			});
		}
	};

	submitKindleInfo = () => {
		const { kindleEmail, selectedKindleDownloadMethod } = this.state;

		this.props.userPreferences
			.saveKindlePreferences(kindleEmail, selectedKindleDownloadMethod)
			.then(() => {
				this.setState({ currentStep: this.steps.kindleOverview });
			});
	};

	submitSelectFormat = () => {
		const { selectedFormat, doSaveAsDefaultFormat } = this.state;

		if (doSaveAsDefaultFormat) {
			this.props.userPreferences.saveDrcDownloadFormatPreference(
				selectedFormat,
			);
		}

		switch (selectedFormat) {
			case requestFormats.kindle:
				return this.goToKindleInfoStep();
			case requestFormats.ePub:
			case requestFormats.pdf:
				return this.goToStandardOverviewStep();
			case requestFormats.audioChapters:
			case requestFormats.audioZip:
			case requestFormats.mp3:
				return this.goToAudioOverviewStep();
		}
	};

	goToStep = step => this.setState({ currentStep: step });

	goToSelectFormatStep = () => this.goToStep(this.steps.selectFormat);

	goToKindleInfoStep = () => this.goToStep(this.steps.kindleInfo);

	goToKindleOverviewStep = () => this.goToStep(this.steps.kindleOverview);

	goToStandardOverviewStep = () => this.goToStep(this.steps.standardOverview);

	goToAudioOverviewStep = () => this.goToStep(this.steps.audioOverview);

	goToAudioDownloadStep = () => this.goToStep(this.steps.audioDownload);

	getModalBodyStyle = () => {
		return { paddingTop: 10 };
	};

	getModalTitle = () => (
		<b>
			<Translate id="download_and_read" />
			&nbsp;{this.getTitleName()}
		</b>
	);

	renderKindleDownloadMethod = kindleDownloadMethod => {
		switch (kindleDownloadMethod) {
			case kindleDownloadMethods.sendToEmail:
				return <Translate id="kindle_method_send_to_email" />;
			case kindleDownloadMethods.sendToKindle:
				return <Translate id="kindle_method_send_to_kindle" />;
		}

		return null;
	};

	renderSelectFormatOption = format => (
		<div>
			<Radio value={format}>{this.getFormatDescription(format)}</Radio>
		</div>
	);

	renderSelectFormatStep = () => {
		const { selectedFormat, doSaveAsDefaultFormat } = this.state;

		return (
			<div>
				<p>
					<Translate id="drc_select_format" />
				</p>
				<Radio.Group
					onChange={e =>
						this.setState({ selectedFormat: e.target.value })
					}
					value={selectedFormat}>
					{this.hasPdfFormat() &&
						this.renderSelectFormatOption(requestFormats.pdf)}
					{this.hasEPubFormat() &&
						this.renderSelectFormatOption(requestFormats.ePub)}
					{this.hasKindleFormat() &&
						this.renderSelectFormatOption(requestFormats.kindle)}
					{this.hasAudioFormat() &&
						this.renderSelectFormatOption(requestFormats.audioZip)}
				</Radio.Group>
				<div id="set-preferred-format-option">
					<Checkbox
						onChange={e =>
							this.setState({
								doSaveAsDefaultFormat: e.target.checked,
							})
						}
						checked={doSaveAsDefaultFormat}>
						<Translate id="drc_set_preferred_format" />
					</Checkbox>
				</div>
			</div>
		);
	};

	renderKindleEmail = () => {
		const { kindleEmail, kindleEmailLocked } = this.state;

		if (kindleEmailLocked) {
			return (
				<div>
					<Input value={kindleEmail} disabled />
					<Button
						onClick={() =>
							this.setState({ changeKindleEmailModalOpen: true })
						}>
						<Translate id="change_email_address" />
					</Button>
				</div>
			);
		}

		return (
			<div>
				<Input
					value={kindleEmail}
					onChange={e =>
						this.setState({ kindleEmail: e.target.value })
					}
				/>
			</div>
		);
	};

	renderKindleDownloadMethodOptions = () => {
		const { selectedKindleDownloadMethod } = this.state;

		return (
			<div>
				<Radio.Group
					onChange={e =>
						this.setState({
							selectedKindleDownloadMethod: e.target.value,
						})
					}
					value={selectedKindleDownloadMethod}>
					<Radio value={kindleDownloadMethods.sendToEmail}>
						{this.renderKindleDownloadMethod(
							kindleDownloadMethods.sendToEmail,
						)}
					</Radio>
					<Radio value={kindleDownloadMethods.sendToKindle}>
						{this.renderKindleDownloadMethod(
							kindleDownloadMethods.sendToKindle,
						)}
					</Radio>
				</Radio.Group>
			</div>
		);
	};

	renderKindleInfoStep = () => {
		return (
			<div>
				<p>
					<Translate id="kindle_additional_info_required" />
				</p>
				<p>
					<Translate id="kindle_in_order_to_send" />:
				</p>
				<ol>
					<li>
						<Translate id="kindle_in_order_to_send_point_one" />
					</li>
					<li>
						<Translate id="kindle_in_order_to_send_point_two" />
					</li>
					<li>
						<Translate id="kindle_in_order_to_send_point_three" />
					</li>
					<li>
						<Translate id="kindle_in_order_to_send_point_four" />
					</li>
				</ol>
				<fieldset>
					<legend>
						<Translate id="kindle_information" />
					</legend>
					<strong>
						<Translate id="kindle_email_address" />:
					</strong>
					{this.renderKindleEmail()}
					<strong>
						<Translate id="download_method" />:
					</strong>
					{this.renderKindleDownloadMethodOptions()}
				</fieldset>
			</div>
		);
	};

	renderSelectedFormat = () => {
		const { selectedFormat } = this.state;
		const formatName = this.getFormatName(selectedFormat);

		return (
			<div>
				<Translate>
					{translate => (
						<span>
							{translate('drc_downloading_message').replace(
								'{0}',
								formatName,
							)}
						</span>
					)}
				</Translate>
				<span>.&nbsp;</span>
				{this.hasMultipleFormatsAvailable() && (
					<a onClick={() => this.goToSelectFormatStep()}>
						<Translate id="change" />
					</a>
				)}
			</div>
		);
	};

	renderKindleOverviewStep = () => {
		const { kindleEmail, selectedKindleDownloadMethod } = this.state;

		return (
			<div>
				<p>{this.renderSelectedFormat()}</p>
				<Translate id="drc_kindle_message" />
				<fieldset>
					<legend>
						<Translate id="drc_kindle_profile" />
						&nbsp;
						<a onClick={() => this.goToKindleInfoStep()}>
							<Translate id="change" />
						</a>
					</legend>
					<p>
						<strong>
							<Translate id="send_to" />
							:&nbsp;
						</strong>
						<span>{kindleEmail}</span>
					</p>
					<p>
						<strong>
							<Translate id="drc_method" />
							:&nbsp;
						</strong>
						<span>
							{this.renderKindleDownloadMethod(
								selectedKindleDownloadMethod,
							)}
						</span>
					</p>
				</fieldset>
			</div>
		);
	};

	renderOpenDrmStandardOverviewStep = () => (
		<div>
			<p>{this.renderSelectedFormat()}</p>
			<p>
				<Translate id="drc_open_drm_message" />
			</p>
		</div>
	);

	renderExpirationDate = () => {
		const { selectedFormat } = this.state;
		const { drcExpirationDate, drcExpirationDays } = this.getFormatInfo(
			selectedFormat,
		);

		if (drcExpirationDate && drcExpirationDate > 0) {
			return (
				<Translate>
					{translate => (
						<span>
							{translate('drc_will_expire_on').replace(
								'{0}',
								drcExpirationDate,
							)}
						</span>
					)}
				</Translate>
			);
		}

		if (drcExpirationDays && drcExpirationDays > 0) {
			return (
				<Translate>
					{translate => (
						<span>
							{translate('drc_will_expire_in').replace(
								'{0}',
								drcExpirationDays,
							)}
						</span>
					)}
				</Translate>
			);
		}

		return null;
	};

	renderAdobeDrmStandardOverviewStep = () => {
		return (
			<div>
				<p>{this.renderSelectedFormat()}</p>
				<p>
					<span>
						<Translate id="drc_standard_message" />
					</span>
					<span>&nbsp;</span>
					<span>
						<a
							href="http://www.adobe.com/products/digitaleditions/"
							rel="noopener noreferrer"
							target="_blank">
							<Translate id="drc_adobe_digital_editions" />
						</a>
					</span>
					<span>.&nbsp;</span>
					<span>
						<Translate id="drc_standard_message_parttwo" />
					</span>
				</p>
				<p>{this.renderExpirationDate()}</p>
			</div>
		);
	};

	renderStandardOverviewStep = () => {
		const { selectedFormat } = this.state;

		if (this.isOpenDrmMethod(selectedFormat)) {
			return this.renderOpenDrmStandardOverviewStep();
		}

		return this.renderAdobeDrmStandardOverviewStep();
	};

	renderAudioOverview = () => (
		<div>
			<p>{this.renderSelectedFormat()}</p>
			<p>
				<Translate id="drc_audio_message" />
			</p>
		</div>
	);

	renderAudioChapterDownloadOption = (chapter, url, fileSize) => (
		<div>
			<a href={url}>{chapter}</a>
			<span>({fileSize})</span>
		</div>
	);

	renderAudioChaptersDownloadOptions = (
		audioChapterUrlByChapter,
		audioChapterFileSizeByChapter,
	) => (
		<div>
			{Object.keys(audioChapterUrlByChapter).map(chapter =>
				this.renderAudioChapterDownloadOption(
					chapter,
					audioChapterUrlByChapter[chapter],
					audioChapterFileSizeByChapter[chapter],
				),
			)}
		</div>
	);

	renderAudioZipDownloadOption = (audioZipUrl, audioZipFileSize) => (
		<div>
			<span>
				<a target="_blank" href={audioZipUrl} rel="noopener noreferrer">
					<Translate id="download" />
				</a>
			</span>
			{audioZipFileSize && (
				<span>
					<i>({audioZipFileSize})</i>
				</span>
			)}
		</div>
	);

	renderAudioDownload = () => {
		const formats = this.getAvailableFormats();
		const zipFormatInfo = formats[0]; //this.getAudioZipFormatInfo();
		const chaptersFormatInfo = formats[0]; //this.getAudioChaptersFormatInfo();

		const { audioZipUrl, audioZipFileSize } = zipFormatInfo;
		const {
			audioChapterUrlByChapter,
			audioChapterFileSizeByChapter,
		} = chaptersFormatInfo;

		if (
			audioChapterUrlByChapter &&
			Object.keys(audioChapterUrlByChapter).length > 0
		) {
			return (
				<div>
					<div>
						<span>
							<b>
								<Translate id="download_option" />
								&nbsp;1)&nbsp;
							</b>
							<Translate id="individual_chapters" />
						</span>
						{this.renderAudioChaptersDownloadOptions(
							audioChapterUrlByChapter,
							audioChapterFileSizeByChapter,
						)}
					</div>
					<div>
						<b>
							<Translate id="download_option" />
							&nbsp;2)&nbsp;
						</b>
						<Translate id="compressed_zip_file_with_all_chapter_mp3_files" />
						{this.renderAudioZipDownloadOption(
							audioZipUrl,
							audioZipFileSize,
						)}
					</div>
				</div>
			);
		}

		return (
			<div>
				<Translate id="compressed_zip_file_with_all_chapter_mp3_files" />
				{this.renderAudioZipDownloadOption(
					audioZipUrl,
					audioZipFileSize,
				)}
			</div>
		);
	};

	renderExternalUrlStep = () => {
		return (
			<div>
				<Translate id="drc_external_url_message" />
			</div>
		);
	};

	renderModalContents = () => {
		const { currentStep } = this.state;

		switch (currentStep) {
			case this.steps.selectFormat:
				return this.renderSelectFormatStep();
			case this.steps.kindleInfo:
				return this.renderKindleInfoStep();
			case this.steps.kindleOverview:
				return this.renderKindleOverviewStep();
			case this.steps.standardOverview:
				return this.renderStandardOverviewStep();
			case this.steps.audioOverview:
				return this.renderAudioOverview();
			case this.steps.audioDownload:
				return this.renderAudioDownload();
			case this.steps.externalUrlOverview:
				return this.renderExternalUrlStep();
		}
	};

	getKindleDownloadMethod = () =>
		this.props.userPreferences.getReviewCopyDownloadPreferences()
			.kindleDownloadMethod;
}

const connectors = [
	connectToUser,
	connectToUserPreferences,
	connectToDownloads,
	connectToAccountOrMembershipRequiredModalContext,
];

export default combineConnectors(connectors)(
	withIsMobile(ReviewCopyDownloadModalAnchor),
);
